import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { DgraphTokenManager } from './dgraphTokenManager';
import { initializeBrowserVisibilityHandler } from './browserVisibilityHandler';

// Track all active WebSocket connections and their client instance
const activeConnections: Map<
  string,
  {
    wsLink?: WebSocketLink;
    client: ExtendedApolloClient;
    tokenManager: DgraphTokenManager;
  }
> = new Map();

// Extended ApolloClient with our custom properties
export interface ExtendedApolloClient extends ApolloClient<NormalizedCacheObject> {
  _httpUrl?: string;
  _wsUrl?: string;
  _token?: string;
  /**
   * Identity of the underlying websocket connection, used as the `activeConnections` key.
   *
   * **Never log this, and never put it in an error message.** It embeds the user's Auth0 access
   * token — see how it is built below — so anything that prints it leaks a bearer credential into a
   * console, and from there into the screenshots and tickets that error paths are expected to be
   * shared through.
   */
  _connectionId?: string;
  _organizationBid?: string;
  _dgraphNamespace?: string;
  /*
   * Whether this client can actually carry a subscription — i.e. its split link has a
   * WebSocketLink. NOT the same thing as having a namespace: the wsLink construction below is
   * wrapped in a try/catch that logs and continues with an HTTP-only link, so a client can hold a
   * namespace and still be unable to subscribe. Consumers deciding whether to subscribe must read
   * this, or they route subscriptions to the HTTP link and get an immediate error.
   */
  _hasWebSocketLink?: boolean;
}

/**
 * Whether `client` can serve the live subscription for THIS org right now.
 *
 * Three things have to be true, and each was a real defect when it was missing:
 *
 * 1. It holds a websocket link. Having a namespace is not the same thing (see
 *    `_hasWebSocketLink`), and a namespaced client with an HTTP-only link routes subscriptions to
 *    the HTTP link and errors immediately.
 * 2. It has not been retired. `stop()`ping a client does not change its markers, so the retire
 *    block below clears `_hasWebSocketLink` as it goes — otherwise a consumer can subscribe against
 *    a stopped client during the window before the replacement commits.
 * 3. It belongs to the org the session is actually in. An org switch happens without a page reload:
 *    the store learns the new org synchronously while the replacement client is still being built,
 *    so a capability-only check would subscribe on the PREVIOUS org's socket, with the previous
 *    org's Dgraph JWT, into a session that has already moved on.
 */
export const canServeLiveSubscription = (
  // `ApolloClient<object>` rather than `<NormalizedCacheObject>`: that is what a bare
  // `useApolloClient()` resolves to, and typing it narrower makes this predicate unusable from
  // the very consumers it exists for.
  client: ApolloClient<object> | null,
  organizationBid?: string,
  dgraphNamespace?: string | number,
): client is ExtendedApolloClient => {
  const candidate = client as ExtendedApolloClient | null;
  if (!candidate?._hasWebSocketLink) return false;
  if (candidate._organizationBid !== organizationBid) return false;
  return candidate._dgraphNamespace === (dgraphNamespace?.toString() || undefined);
};

// Create a singleton instance
let apolloClientInstance: ExtendedApolloClient | null = null;

/*
 * Serialises client creation. `ProtectedGate`'s effect legitimately runs once per auth-metadata
 * stage (access token, then orgBID, then dgraph_namespace), and this factory awaits between
 * tearing the old client down and standing the new one up. Unserialised, those runs interleaved:
 * one failing production boot recreated the client FIVE times inside a second, each cycle
 * stop()ping its predecessor and erroring the live subscription. Chaining makes every call produce
 * a clean create -> swap -> stop sequence instead of overlapping stop/create pairs.
 */
let creationChain: Promise<unknown> = Promise.resolve();

/*
 * Teardown runs on the SAME chain as creation. Without this, the module's invariant — that every
 * mutation of `apolloClientInstance`/`activeConnections` is serialised — would only be nearly true:
 * a cleanup landing between a build's awaits would be undone by that build's own swap, resurrecting
 * a live client and an authenticated websocket after the caller believed teardown had finished.
 */
const onCreationChain = <T>(work: () => Promise<T>): Promise<T> => {
  const run = creationChain.then(work, work);
  creationChain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
};

// Function to forcibly close a WebSocket connection with subscription cleanup
const forceCloseWebSocket = async (connectionId: string): Promise<boolean> => {
  const connection = activeConnections.get(connectionId);
  if (!connection) return false;

  try {
    connection.tokenManager.cleanup();
    activeConnections.delete(connectionId);
    return true;
  } catch (error) {
    console.error('Error closing WebSocket connection:', error);
    return false;
  }
};

const buildGraphQLApolloClient = async (
  httpUrl: string,
  wsUrl: string,
  userAccessToken: string,
  organizationBid: string,
  dgraphNamespace?: string,
): Promise<ExtendedApolloClient> => {
  if (!httpUrl) {
    throw new Error('liveGraphQLEndpoint not found');
  }

  if (!wsUrl) {
    throw new Error('liveGraphQLSubscriptions not found');
  }

  if (!organizationBid) {
    throw new Error('organizationBid is required for Dgraph operations');
  }

  /*
   * Includes `httpUrl` so this id matches the identity the singleton fast path below actually
   * compares. It did not, so two calls differing only in `httpUrl` produced the SAME id — and with
   * stop-after-swap, registering the replacement first then overwrote the outgoing entry.
   */
  // Contains the access token, so it is a CREDENTIAL: never log it. See `_connectionId`.
  const connectionId = `${httpUrl}|${wsUrl}|${userAccessToken}|${organizationBid}|${dgraphNamespace || 'no-namespace'}`;

  // Return existing instance if URLs, token, organizationBid, and dgraphNamespace match
  if (apolloClientInstance) {
    const urlsMatch =
      apolloClientInstance._httpUrl === httpUrl && apolloClientInstance._wsUrl === wsUrl;
    const tokenMatches = apolloClientInstance._token === userAccessToken;
    const bidMatches = apolloClientInstance._organizationBid === organizationBid;
    const namespaceMatches =
      apolloClientInstance._dgraphNamespace === (dgraphNamespace || undefined);

    if (urlsMatch && tokenMatches && bidMatches && namespaceMatches) {
      return apolloClientInstance;
    }

    if (
      urlsMatch &&
      tokenMatches &&
      bidMatches &&
      !apolloClientInstance._dgraphNamespace &&
      dgraphNamespace
    ) {
      console.log(
        'Dgraph namespace became available, recreating Apollo client to enable WebSocket connections',
      );
    }
  }

  /*
   * The outgoing client is captured but NOT torn down yet. It used to be stopped here, before the
   * replacement existed — so for the duration of the awaits below the ApolloProvider was handing
   * consumers a stopped client, and any subscription attempted in that window errored immediately.
   * It is stopped after the swap, at the end of this function.
   */
  const previousClient = apolloClientInstance;
  const previousConnectionId = previousClient?._connectionId;
  const previousConnection = previousConnectionId
    ? activeConnections.get(previousConnectionId)
    : undefined;

  // Create HTTP link
  const httpLink = createHttpLink({
    uri: httpUrl,
  });

  // Derive token URL from httpUrl (e.g. ".../api/dgraph" -> ".../api/dgraph/token/:orgBid")
  const getTokenUrl = (orgBid: string) => `${httpUrl}/token/${orgBid}`;

  // Create token manager
  const tokenManager = new DgraphTokenManager(
    wsUrl,
    organizationBid,
    userAccessToken,
    dgraphNamespace || '',
    getTokenUrl,
  );

  // Initialize browser visibility handler to handle tab wake scenarios
  initializeBrowserVisibilityHandler(tokenManager);

  // Create WebSocket link only if dgraph namespace is available
  let wsLink: WebSocketLink | null = null;
  if (dgraphNamespace) {
    try {
      const wsClient = await tokenManager.createWebSocketClient();
      wsLink = new WebSocketLink(wsClient);
    } catch (error) {
      /*
       * `error`, not `warn`, and deliberately worded so it cannot be mistaken for the benign
       * every-boot "No dgraph namespace provided" line below. This branch means the org HAS a
       * namespace but the socket could not be built (a malformed wss URL, a CSP blocking the host),
       * which is terminal for this client: nothing rebuilds it, so the session has no live data.
       */
      console.error(
        `Dgraph websocket link could NOT be built for namespace ${dgraphNamespace} — live subscriptions are unavailable for this session:`,
        error,
      );
    }
  } else {
    console.debug('No dgraph namespace provided, subscriptions will not be available');
  }

  // Create an authentication middleware for HTTP requests
  const authLink = setContext((_, { headers }) => {
    const isMiddlewareEndpoint = httpUrl.includes('/api/dgraph');

    if (isMiddlewareEndpoint) {
      return {
        headers: {
          ...headers,
          Authorization: `Bearer ${userAccessToken}`,
          'Content-Type': 'application/json',
        },
        uri: `${httpUrl}?org_bid=${organizationBid}`,
      };
    } else {
      return {
        headers: {
          ...headers,
          'X-Auth-Token':
            (typeof process !== 'undefined' && process.env?.REACT_APP_DGRAPH_X_AUTH_TOKEN) || '',
        },
      };
    }
  });

  const link = wsLink
    ? split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
          );
        },
        wsLink,
        authLink.concat(httpLink),
      )
    : authLink.concat(httpLink);

  const cache = new InMemoryCache({
    addTypename: false,
    resultCaching: false,
  });

  const client = new ApolloClient({
    link: link,
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'no-cache',
      },
      mutate: {
        fetchPolicy: 'no-cache',
      },
    },
    queryDeduplication: false,
  }) as ExtendedApolloClient;

  client._httpUrl = httpUrl;
  client._wsUrl = wsUrl;
  client._token = userAccessToken;
  client._connectionId = connectionId;
  client._organizationBid = organizationBid;
  client._dgraphNamespace = dgraphNamespace || undefined;
  client._hasWebSocketLink = !!wsLink;

  apolloClientInstance = client;

  activeConnections.set(connectionId, {
    wsLink: wsLink || undefined,
    client,
    tokenManager,
  });

  /*
   * Now — and only now — retire the client we replaced. The guard matters: `connectionId` omits
   * `httpUrl` while the singleton fast path above compares it, so a same-socket/different-http-url
   * call would give the new client the same connection id as the old one. Closing blindly by id
   * would then tear down the connection we just registered.
   */
  if (previousClient && previousClient !== client) {
    /*
     * Retire by the reference captured before the swap, not by re-reading the map: the `set` above
     * has already overwritten the entry when the two connection ids collide, and re-reading would
     * then either close the connection just registered or (with a guard) silently skip cleanup and
     * leak the outgoing tokenManager's socket and refresh timers.
     */
    if (previousConnection) {
      try {
        previousConnection.tokenManager.cleanup();
      } catch (error) {
        console.error('Error closing WebSocket connection:', error);
      }
      if (
        previousConnectionId &&
        activeConnections.get(previousConnectionId) === previousConnection
      ) {
        activeConnections.delete(previousConnectionId);
      }
    }
    // A stopped client must stop advertising that it can carry a subscription, or a consumer can
    // subscribe against it in the window before the replacement commits.
    previousClient._hasWebSocketLink = false;
    previousClient.stop();
  }

  return client;
};

/**
 * Create (or return) the singleton Apollo client for this session's Dgraph endpoints.
 *
 * Concurrent calls are chained rather than run in parallel, so create/stop cycles cannot overlap.
 * Same-parameters calls still return the same instance.
 */
const createGraphQLApolloClient = (
  httpUrl: string,
  wsUrl: string,
  userAccessToken: string,
  organizationBid: string,
  dgraphNamespace?: string,
): Promise<ExtendedApolloClient> => {
  // Runs after whatever is already in flight, whether that succeeded or failed — one call's
  // failure must not wedge the chain for every later call.
  return onCreationChain(() =>
    buildGraphQLApolloClient(httpUrl, wsUrl, userAccessToken, organizationBid, dgraphNamespace),
  );
};

// Utility function to explicitly clean up the Apollo client
const doCleanupApolloClient = async (): Promise<void> => {
  if (apolloClientInstance) {
    if (apolloClientInstance._connectionId) {
      await forceCloseWebSocket(apolloClientInstance._connectionId);
    }

    apolloClientInstance.stop();
    apolloClientInstance = null;
  }

  const connectionPromises = Array.from(activeConnections.keys()).map(id =>
    forceCloseWebSocket(id),
  );
  await Promise.all(connectionPromises);
  activeConnections.clear();
};

const doForceCloseAllWebSockets = async (): Promise<void> => {
  const connectionPromises = Array.from(activeConnections.keys()).map(id =>
    forceCloseWebSocket(id),
  );
  await Promise.all(connectionPromises);
  activeConnections.clear();
};

/** Explicitly tear down the singleton client and every tracked websocket. */
export const cleanupApolloClient = (): Promise<void> => onCreationChain(doCleanupApolloClient);

/** Force close every tracked websocket connection. */
export const forceCloseAllWebSockets = (): Promise<void> =>
  onCreationChain(doForceCloseAllWebSockets);

export default createGraphQLApolloClient;
